/**
 * GameView — the root UI component (M8-C). Composes the full tree from
 * ui/31 §2: PlayerPanel × 2, Board (Table → Lane → Cell → BoardCard), Hand,
 * EventLog, ReadyButton, and a targeted ChoicePrompt.
 *
 * `GameViewProps` is the frozen seam between the store and the UI (M8-A) —
 * this signature MUST NOT change. GameView reads the redacted state and a
 * UI-only playback context supplied by App: no store, sockets, or game logic
 * live here (ui/31 §1, D22/R9). Its local state only tracks hand selection and
 * the shared pointer-drag interaction.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, ReactElement, ReactNode } from 'react';
import type { Action, CellId, GameState, InstanceId, PlayerId, Side, TableId, TargetCandidate, TargetStepSelection } from '@cards/engine';
import { availableEnergy, buildPublicTargetContext, effectiveCardCost, enumeratePlanningActions, targetCandidateKey } from '@cards/engine';
import { getCardModeLabel, getCardPlanningStepLabel, registry } from '@cards/cards';
import {
  commitCompleteAction,
  computeStepView,
  isBoardTargetable as isBoardTargetableCard,
  type BoardStepView,
} from './planningBoardTargeting.js';
import type { ActionSink, CanApply, LogEntry } from '../types.js';
import type { SerializedChoice } from '../../server/protocol.js';
import { Board } from './Board.js';
import { CardFlightGhost, createCardFlight, type CardFlight, type CardFlightApi, type CardFlightDestination, type FlightOrigin } from './cardFlight.js';
import type { CardDragMotion } from './cardMotion.js';
import { ChoicePrompt } from './ChoicePrompt.js';
import { DeckViewer } from './DeckViewer.js';
import { resolveDropCandidate } from './dropZones.js';
import { EventLog } from './EventLog.js';
import { Hand, type HandCardData } from './Hand.js';
import { PlayerPanel } from './PlayerPanel.js';
import { ReadyButton } from './ReadyButton.js';
import { SpellPlan } from './SpellPlan.js';
import { Button } from './kit/index.js';
import { COLORS } from './kit/tokens.js';
import { useTurnPlaybackSelector, type TurnBanner } from './TurnPlayback.js';
import { togglePlaybackSpeed, usePlaybackSpeed } from './playbackSpeed.js';
import { playerUiMechanics } from './mechanics.js';
import { deckPassivePresentationForPlayer } from './deckPassiveView.js';

type PlanCardAction = Extract<Action, { kind: 'PlanCard' }>;

export interface GameViewProps {
  /** Redacted authoritative snapshot for `viewerId`. */
  state: GameState;
  /** Whose view this is (own vs opponent panels/hand). */
  viewerId: PlayerId;
  /** Event log for `<EventLog>` (R14). */
  log: readonly LogEntry[];
  /** Choice prompt when the server awaits this viewer's `Resolve`. */
  pendingChoice?: SerializedChoice;
  /** Emit a player intent (PlanCard / UnplanCard / Ready / Resolve). */
  onAction: ActionSink;
  /** Pure local affordance gate for disabling/highlighting (ui/31 §3). */
  canApply?: CanApply;
  /** Revealed prepared-deck ids, used only to hide irrelevant UI mechanics. */
  deckDefinitionIds?: Readonly<Partial<Record<PlayerId, string>>>;
  /** Public display names for readable event-log participants. */
  playerNames?: Readonly<Partial<Record<PlayerId, string>>>;
}

/** Route-owned actions rendered only when the playback snapshot reaches its result. */
export const MatchResultActionsContext = createContext<ReactNode>(undefined);

/** Pixel confetti a win throws (03 §4.14); kit.css lays each one out. */
export const MATCH_CONFETTI_COUNT = 24;

/** The turn's steps as the HUD names them (02 §5.3). */
const HUD_PHASES = ['План', 'Раскрытие', 'Бой', 'Итог'] as const;

/** Which HUD step a moment of the round is: planning, then the playback beats. */
function hudPhaseIndex(planning: boolean, isPlaying: boolean, banner: string | undefined): number {
  if (!isPlaying) return planning ? 0 : 3;
  if (banner === 'combat' || banner === 'deaths') return 2;
  if (banner === 'round_end') return 3;
  return 1;
}

/**
 * The turn HUD (02 §5.3): the round, the four steps of the turn with the
 * current one lit, and «ПРОПУСТИТЬ» while the round plays back. Its own store
 * subscription: notices flip on every playback event, and routing them through
 * GameView used to re-render the whole game tree (board, hand, panels) twice
 * per event.
 */
/**
 * Keys that act on the playback while it runs (04 §6.3): Space skips the
 * batch, «2» flips the speed. Typing, a focused control and an open dialog keep
 * their keys — Space on a focused card or button is that element's press.
 */
function playbackKeyTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return true;
  if (target.isContentEditable) return false;
  return target.closest('input, textarea, select, button, a[href], [role="button"], [role="dialog"]') === null;
}

/** The flying phase plate over the field (03 §4.1); its colour rides data-phase. */
function PhaseBanner({ banner, label }: { banner: TurnBanner; label: string }): ReactElement {
  return (
    <div className="cards-phase-banner" data-testid="turn-phase-banner" data-phase={banner} aria-hidden="true">
      {label}
    </div>
  );
}

function PlaybackHud({ round, planning }: { round: number; planning: boolean }): ReactElement {
  const playback = useTurnPlaybackSelector((visuals) => ({
    isPlaying: visuals.isPlaying,
    banner: visuals.banner,
    notice: visuals.notice,
    skip: visuals.skip,
  }));
  const speed = usePlaybackSpeed();
  const { isPlaying, skip } = playback;
  useEffect(() => {
    if (!isPlaying) return undefined;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
      if (!playbackKeyTarget(event.target)) return;
      if (event.key === ' ') {
        event.preventDefault();
        skip();
      } else if (event.key === '2') {
        event.preventDefault();
        togglePlaybackSpeed();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isPlaying, skip]);
  const playbackBanner = playback.banner === undefined
    ? undefined
    : ({
        reveal: { label: 'РАСКРЫТИЕ', color: COLORS.selection },
        effects: { label: 'ЭФФЕКТЫ', color: COLORS.positive },
        combat: { label: 'БОЙ', color: COLORS.attack },
        deaths: { label: 'СМЕРТИ', color: COLORS.negative },
        round_end: { label: 'КОНЕЦ РАУНДА', color: COLORS.textMutedOnDark },
      } as const)[playback.banner];
  const current = hudPhaseIndex(planning, playback.isPlaying, playback.banner);
  return (
    <>
      <div className="cards-playback-hud" data-testid="turn-playback-hud" data-playing={playback.isPlaying ? 'true' : undefined}>
        {/* Keyed by the round, so a new round remounts the number and it hops in (03 §4.1). */}
        <span key={round} className="cards-playback-hud-round" data-testid="turn-hud-round">Раунд {round}</span>
        <ol className="cards-playback-hud-phases" aria-label="Шаги хода">
          {HUD_PHASES.map((label, index) => (
            <li
              key={label}
              className="cards-playback-hud-phase"
              data-state={index < current ? 'done' : index === current ? 'current' : 'next'}
              aria-current={index === current ? 'step' : undefined}
            >
              {label}
            </li>
          ))}
        </ol>
        {playback.isPlaying ? (
          <div
            className="cards-turn-playback-banner"
            data-testid="turn-playback-banner"
            role="status"
            aria-live="polite"
            data-phase={playback.banner}
            // Phase color is runtime playback state; geometry/presentation is static.
            style={{
              '--cards-playback-tone': playbackBanner?.color ?? COLORS.selection,
            } as CSSProperties}
          >
            <span>{playbackBanner?.label ?? 'СОБЫТИЯ'}</span>
            <Button
              variant="secondary"
              size="compact"
              className="cards-turn-playback-speed"
              data-testid="turn-playback-speed"
              aria-pressed={speed === 2}
              aria-label={`Скорость ×${speed}`}
              onClick={togglePlaybackSpeed}
            >
              ×{speed}
            </Button>
            <Button
              variant="secondary"
              size="compact"
              className="cards-turn-playback-skip"
              data-testid="turn-playback-skip"
              onClick={playback.skip}
            >
              ПРОПУСТИТЬ
            </Button>
          </div>
        ) : null}
        {playback.isPlaying && playback.notice !== undefined ? (
          <div
            className="cards-playback-notice"
            data-testid="turn-playback-notice"
            data-kind={playback.notice.kind}
            role="status"
            aria-live="polite"
            // Notice tone depends on the current playback event; its
            // position is static because phase banners are hidden.
            style={{
              '--cards-playback-tone': playback.notice.kind === 'timed' ? COLORS.negative : COLORS.selection,
            } as CSSProperties}
          >
            {playback.notice.text}
          </div>
        ) : null}
      </div>
      {/* Only at ×1: at ×2 the beats are too short to carry it, and the lit step
          in the strip already says the same (03 §4.1). Keyed by the phase, so
          each new phase replays the flight. */}
      {playback.isPlaying && playbackBanner !== undefined && playback.banner !== undefined && speed === 1 ? (
        <PhaseBanner key={playback.banner} banner={playback.banner} label={playbackBanner.label} />
      ) : null}
    </>
  );
}

export function GameView(props: GameViewProps): ReactElement {
  const { state, viewerId, log, pendingChoice, onAction, canApply, deckDefinitionIds, playerNames } = props;
  // The HUD above subscribes on its own; the view itself only needs the coarse
  // playing flag, so per-cue playback steps skip this whole tree.
  const playbackIsPlaying = useTurnPlaybackSelector((visuals) => visuals.isPlaying);
  const oneShotLineup = useTurnPlaybackSelector((visuals) => visuals.oneShotLineup);
  const drawCue = useTurnPlaybackSelector((visuals) => visuals.drawCue);
  const [selectedHandId, setSelectedHandId] = useState<InstanceId | undefined>(undefined);
  const [draggingHandId, setDraggingHandId] = useState<InstanceId | undefined>(undefined);
  const [draggingStagedId, setDraggingStagedId] = useState<InstanceId | undefined>(undefined);
  const [targeting, setTargeting] = useState<
    { instanceId: InstanceId; previous: readonly TargetStepSelection[]; current: readonly TargetCandidate[] } | undefined
  >(undefined);
  const [deckOpen, setDeckOpen] = useState(false);
  const [cardFlights, setCardFlights] = useState<readonly CardFlight[]>([]);
  const expectedFlightDestinationsRef = useRef(new Map<InstanceId, CardFlightDestination>());
  const openDeckViewer = useCallback(() => setDeckOpen(true), []);
  const closeDeckViewer = useCallback(() => setDeckOpen(false), []);
  const cancelCardFlight = useCallback((instanceId: InstanceId) => {
    expectedFlightDestinationsRef.current.delete(instanceId);
    setCardFlights((previous) => previous.filter((flight) => flight.card.instance.id !== instanceId));
  }, []);
  const expectCardFlight = useCallback((instanceId: InstanceId, destination: CardFlightDestination) => {
    expectedFlightDestinationsRef.current.set(instanceId, destination);
  }, []);
  const landCardFlight = useCallback((id: number) => {
    setCardFlights((previous) => previous.map((flight) =>
      flight.id === id ? Object.assign(flight, { landing: true }) : flight));
  }, []);
  const finishCardFlight = useCallback((id: number) => {
    setCardFlights((previous) => previous.filter((flight) => flight.id !== id));
  }, []);
  const viewerPlayer = state.players[viewerId];
  const planningEnabled = viewerPlayer !== undefined
    && !playbackIsPlaying
    && !viewerPlayer.ready
    && state.phase === 'planning';
  // Resolution playback replaces the snapshot several times per visible cue.
  // Planning is locked throughout it, so rebuilding the full public target
  // projection for every intermediate snapshot is pure main-thread overhead.
  const targetContext = useMemo(
    () => planningEnabled ? buildPublicTargetContext(state, viewerId, registry) : undefined,
    [planningEnabled, state, viewerId],
  );

  // Drop a board-targeting session that no longer applies (turn locked, card
  // left the hand, or it got staged/committed) so the board can't get stuck.
  useEffect(() => {
    if (targeting === undefined) return;
    const player = state.players[viewerId];
    const disabled = playbackIsPlaying || player?.ready === true || state.phase !== 'planning';
    const inHand = player?.hand.includes(targeting.instanceId) ?? false;
    const staged = player?.staged.some((placement) => placement.instanceId === targeting.instanceId) ?? false;
    if (disabled || !inHand || staged) setTargeting(undefined);
  }, [targeting, state, viewerId, playbackIsPlaying]);

  // Esc puts back a chosen card and drops a targeting session (Q6, 04 §6.3).
  // A dialog keeps its own Esc: it closes itself first.
  const choosing = selectedHandId !== undefined || targeting !== undefined;
  useEffect(() => {
    if (!choosing) return undefined;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape' || event.defaultPrevented) return;
      if (event.target instanceof Element && event.target.closest('[role="dialog"]') !== null) return;
      setSelectedHandId(undefined);
      setTargeting(undefined);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [choosing]);

  // Rebuilt only when the snapshot changes. Hand keys its own reorder memo on
  // this array's identity, so handing it a fresh array on every render — a hover
  // opening, a playback cue, a targeting step — silently defeated that memo and
  // rebuilt the hand's order maps on every one of those renders instead.
  const handCards = useMemo<HandCardData[]>(() => {
    const built: HandCardData[] = [];
    for (const instanceId of viewerPlayer?.hand ?? []) {
      const instance = state.instances[instanceId];
      if (instance === undefined) continue;
      const def = registry.get(instance.defId);
      built.push({
        instance,
        cost: effectiveCardCost(state, registry, instanceId),
        type: def?.type,
        hasStats: def?.baseStats !== undefined,
        keywords: def?.keywords ?? [],
      });
    }
    return built;
  }, [state, viewerPlayer]);

  if (viewerPlayer === undefined) {
    return (
      <div className="cards-game-view cards-game-view-error" data-testid="game-view">
        Неизвестный игрок: {viewerId}
      </div>
    );
  }

  const opponentId = Object.keys(state.players).find((id) => id !== viewerId);
  const opponentPlayer = opponentId !== undefined ? state.players[opponentId] : undefined;
  const viewerMechanics = playerUiMechanics(state, viewerId, deckDefinitionIds?.[viewerId]);
  const opponentMechanics = opponentId === undefined
    ? undefined
    : playerUiMechanics(state, opponentId, deckDefinitionIds?.[opponentId]);
  const viewerDeckPassive = deckPassivePresentationForPlayer(state, viewerId);
  const opponentDeckPassive = opponentId === undefined
    ? undefined
    : deckPassivePresentationForPlayer(state, opponentId);
  const viewerSide: Side = viewerPlayer.side;
  const opponentSide: Side = viewerSide === 'A' ? 'B' : 'A';
  const planningDisabled = !planningEnabled;
  const viewerLineup = oneShotLineup.filter((card) => card.playerId === viewerId);
  const viewerDrawCue = drawCue?.playerId === viewerId ? drawCue : undefined;
  const opponentLineup = opponentId === undefined
    ? []
    : oneShotLineup.filter((card) => card.playerId === opponentId);

  const stagedByCell: Partial<Record<CellId, InstanceId[]>> = {};
  const stagedHandIds = new Set<InstanceId>();
  for (const placement of viewerPlayer.staged) {
    if ('cellId' in placement) (stagedByCell[placement.cellId] ??= []).push(placement.instanceId);
    stagedHandIds.add(placement.instanceId);
  }

  /** Every released carried card becomes one flight homing onto its new place. */
  function launchCardFlight(instanceId: InstanceId, motion: CardDragMotion, origin: FlightOrigin): void {
    const destination = expectedFlightDestinationsRef.current.get(instanceId) ?? { kind: 'auto' };
    expectedFlightDestinationsRef.current.delete(instanceId);
    const card = handCards.find(({ instance }) => instance.id === instanceId);
    if (card === undefined) return;
    const flight = createCardFlight(card, motion, origin, destination);
    setCardFlights((previous) => [
      ...previous.filter((other) => other.card.instance.id !== instanceId),
      flight,
    ]);
  }

  const flightApi: CardFlightApi = {
    launch: launchCardFlight,
    cancel: cancelCardFlight,
    flyingIds: new Set(cardFlights.filter((flight) => !flight.landing).map((flight) => flight.card.instance.id)),
    landingIds: new Set(cardFlights.filter((flight) => flight.landing).map((flight) => flight.card.instance.id)),
  };

  const plannedEnergy = viewerPlayer.staged.reduce((sum, placement) => {
    return sum + (effectiveCardCost(state, registry, placement.instanceId) ?? 0);
  }, 0);
  // Spendable energy shown beside the viewer's health: only the in-progress plan is
  // subtracted, so a committed plan in resolution/playback isn't charged twice.
  const remainingEnergy = Math.max(
    0,
    availableEnergy(state, viewerPlayer) - (state.phase === 'planning' ? plannedEnergy : 0),
  );
  const planningActionCache = new Map<InstanceId, PlanCardAction[]>();
  const directBoardActionCache = new Map<InstanceId, ReadonlyMap<CellId, PlanCardAction> | undefined>();
  const directBoardTableActionCache = new Map<InstanceId, ReadonlyMap<TableId, PlanCardAction> | undefined>();
  const directUntargetedActionCache = new Map<InstanceId, PlanCardAction | undefined>();
  const boardTargetableCache = new Map<InstanceId, boolean>();

  function selectHandCard(instanceId: InstanceId): void {
    if (planningDisabled) return;
    if (selectedHandId !== instanceId && isBoardTargetable(instanceId)) {
      const first = firstStepView(instanceId);
      if (first !== undefined && first.candidateModes.length > 0) {
        setSelectedHandId(undefined);
        setTargeting({ instanceId, previous: [], current: [] });
        return;
      }
    }
    setTargeting(undefined);
    setSelectedHandId((previous) => previous === instanceId ? undefined : instanceId);
  }

  function legalPlanningActions(instanceId: InstanceId): PlanCardAction[] {
    if (planningDisabled) return [];
    const cached = planningActionCache.get(instanceId);
    if (cached !== undefined) return cached;
    // The public enumerator intentionally grows plans and therefore rejects an
    // already-staged instance. For an interactive correction, enumerate the
    // replacement against a view with only that old entry removed; canApply
    // below still authoritatively checks the resulting PlanCard on live state.
    const planningPlayer = state.players[viewerId];
    if (planningPlayer === undefined) return [];
    const replacing = planningPlayer.staged.some((plan) => plan.instanceId === instanceId);
    const enumerationState: GameState = replacing ? {
      ...state,
      players: {
        ...state.players,
        [viewerId]: {
          ...planningPlayer,
          staged: planningPlayer.staged.filter((plan) => plan.instanceId !== instanceId),
        },
      },
    } : state;
    const actions = enumeratePlanningActions(enumerationState, registry, viewerId, instanceId);
    const legal = canApply === undefined ? actions : actions.filter(canApply);
    planningActionCache.set(instanceId, legal);
    return legal;
  }

  function candidateCellId(candidate: TargetCandidate): CellId | undefined {
    if (candidate.kind === 'cell') {
      return state.board.cells[candidate.cellId] === undefined ? undefined : candidate.cellId as CellId;
    }
    if (candidate.kind === 'unit' || candidate.kind === 'card') {
      const cellId = state.instances[candidate.instanceId]?.cellId;
      return cellId !== undefined && state.board.cells[cellId] !== undefined ? cellId : undefined;
    }
    return undefined;
  }

  /**
   * A PlanCard reduces to "one board cell" when every selection resolves to the
   * same on-board cell: single-step unit/cell/card targets, and overlay cards
   * (colony_devourer/overgulper) whose `host` + `placement` both point at the
   * host's cell. Anything touching a different cell, or a non-cell target
   * (table/player/mode), returns undefined and falls back to the table drop
   * path or the planning wizard.
   */
  function actionCellId(action: PlanCardAction): CellId | undefined {
    const selections = action.selections ?? [];
    if (selections.length === 0) return undefined;
    let resolved: CellId | undefined;
    for (const step of selections) {
      if (step.selection.length !== 1) return undefined;
      const cellId = candidateCellId(step.selection[0]!);
      if (cellId === undefined) return undefined;
      if (resolved === undefined) resolved = cellId;
      else if (resolved !== cellId) return undefined;
    }
    return resolved;
  }

  function directBoardActions(instanceId: InstanceId): ReadonlyMap<CellId, PlanCardAction> | undefined {
    if (directBoardActionCache.has(instanceId)) return directBoardActionCache.get(instanceId);
    const actions = legalPlanningActions(instanceId);
    const entries: Array<readonly [CellId, PlanCardAction]> = [];
    for (const action of actions) {
      const cellId = actionCellId(action);
      if (cellId === undefined) {
        directBoardActionCache.set(instanceId, undefined);
        return undefined;
      }
      entries.push([cellId, action]);
    }
    if (entries.length === 0 || new Set(entries.map(([cellId]) => cellId)).size !== entries.length) {
      directBoardActionCache.set(instanceId, undefined);
      return undefined;
    }
    const direct = new Map(entries);
    directBoardActionCache.set(instanceId, direct);
    return direct;
  }

  function intentForCell(instanceId: InstanceId, clickedCellId: CellId): { selectable: boolean; action?: Action } {
    if (planningDisabled) return { selectable: false };
    const action = directBoardActions(instanceId)?.get(clickedCellId);
    if (action !== undefined) return { selectable: true, action };
    // Board-targetable cards: highlight the valid first-step targets so a drop
    // onto one starts an on-board targeting session (category C).
    if (isBoardTargetable(instanceId) && firstStepView(instanceId)?.candidateCells.has(clickedCellId) === true) {
      return { selectable: true };
    }
    return { selectable: false };
  }

  function actionTableId(action: PlanCardAction): TableId | undefined {
    const selections = action.selections ?? [];
    if (selections.length !== 1 || selections[0]!.selection.length !== 1) return undefined;
    const candidate = selections[0]!.selection[0]!;
    return candidate.kind === 'table' ? candidate.tableId : undefined;
  }

  /**
   * Location-targeting cards (slimefall/contamination_map) whose every legal
   * PlanCard picks exactly one whole location. When defined, the card is played
   * by dragging/clicking a location instead of opening the planning wizard.
   */
  function directBoardTableActions(instanceId: InstanceId): ReadonlyMap<TableId, PlanCardAction> | undefined {
    if (directBoardTableActionCache.has(instanceId)) return directBoardTableActionCache.get(instanceId);
    const actions = legalPlanningActions(instanceId);
    const entries: Array<readonly [TableId, PlanCardAction]> = [];
    for (const action of actions) {
      const tableId = actionTableId(action);
      if (tableId === undefined) {
        directBoardTableActionCache.set(instanceId, undefined);
        return undefined;
      }
      entries.push([tableId, action]);
    }
    if (entries.length === 0 || new Set(entries.map(([tableId]) => tableId)).size !== entries.length) {
      directBoardTableActionCache.set(instanceId, undefined);
      return undefined;
    }
    const direct = new Map(entries);
    directBoardTableActionCache.set(instanceId, direct);
    return direct;
  }

  function intentForTable(instanceId: InstanceId, tableId: TableId): { selectable: boolean; action?: Action } {
    if (planningDisabled) return { selectable: false };
    const action = directBoardTableActions(instanceId)?.get(tableId);
    return action === undefined ? { selectable: false } : { selectable: true, action };
  }

  /**
   * Targetless cards (a SPELL without planning steps, an armed REACTION):
   * exactly one legal PlanCard with no selections. Such a card is played by
   * dropping or clicking anywhere on the board instead of a confirm-only
   * wizard modal.
   */
  function directUntargetedAction(instanceId: InstanceId): PlanCardAction | undefined {
    if (planningDisabled) return undefined;
    if (directUntargetedActionCache.has(instanceId)) return directUntargetedActionCache.get(instanceId);
    const actions = legalPlanningActions(instanceId);
    const action = actions.length === 1 && (actions[0]!.selections?.length ?? 0) === 0
      ? actions[0]
      : undefined;
    directUntargetedActionCache.set(instanceId, action);
    return action;
  }

  function playUntargeted(instanceId: InstanceId): boolean {
    const action = directUntargetedAction(instanceId);
    if (action !== undefined && (canApply === undefined || canApply(action))) {
      if (draggingHandId === instanceId || draggingStagedId === instanceId) {
        expectCardFlight(instanceId, { kind: 'none' });
      }
      onAction(action);
      setSelectedHandId(undefined);
      setDraggingHandId(undefined);
      setDraggingStagedId(undefined);
      return true;
    }
    return false;
  }

  function planAtTable(instanceId: InstanceId, tableId: TableId): boolean {
    if (planningDisabled) return false;
    const action = directBoardTableActions(instanceId)?.get(tableId);
    if (action !== undefined && (canApply === undefined || canApply(action))) {
      if (draggingHandId === instanceId || draggingStagedId === instanceId) {
        expectCardFlight(instanceId, { kind: 'none' });
      }
      onAction(action);
      setSelectedHandId(undefined);
      setDraggingHandId(undefined);
      setDraggingStagedId(undefined);
      return true;
    }
    return false;
  }

  function handleTableClick(tableId: TableId): void {
    if (planningDisabled) return;
    if (selectedHandId !== undefined) planAtTable(selectedHandId, tableId);
  }

  function definitionForInstance(instanceId: InstanceId) {
    const defId = state.instances[instanceId]?.defId;
    return defId === undefined ? undefined : registry.get(defId);
  }

  function isBoardTargetable(instanceId: InstanceId): boolean {
    if (targetContext === undefined) return false;
    const cached = boardTargetableCache.get(instanceId);
    if (cached !== undefined) return cached;
    let result = false;
    if (directBoardActions(instanceId) === undefined && directBoardTableActions(instanceId) === undefined) {
      const definition = definitionForInstance(instanceId);
      result = definition !== undefined
        && isBoardTargetableCard(definition, targetContext, state, legalPlanningActions(instanceId));
    }
    boardTargetableCache.set(instanceId, result);
    return result;
  }

  function firstStepView(instanceId: InstanceId): BoardStepView | undefined {
    if (targetContext === undefined) return undefined;
    const definition = definitionForInstance(instanceId);
    if (definition === undefined) return undefined;
    return computeStepView(definition, targetContext, state, legalPlanningActions(instanceId), [], []);
  }

  const activeTargeting = targeting !== undefined
    && !planningDisabled
    && viewerPlayer.hand.includes(targeting.instanceId)
    && !stagedHandIds.has(targeting.instanceId)
    ? targeting
    : undefined;
  const targetingDefinition = activeTargeting === undefined ? undefined : definitionForInstance(activeTargeting.instanceId);
  const targetingView = activeTargeting === undefined || targetingDefinition === undefined || targetContext === undefined
    ? undefined
    : computeStepView(
        targetingDefinition,
        targetContext,
        state,
        legalPlanningActions(activeTargeting.instanceId),
        activeTargeting.previous,
        activeTargeting.current,
      );
  const targetingStepLabel = activeTargeting === undefined || targetingView === undefined
    ? undefined
    : getCardPlanningStepLabel(state.instances[activeTargeting.instanceId]?.defId ?? '', targetingView.stepId)?.label
      ?? `Шаг ${targetingView.stepIndex + 1} из ${targetingView.totalSteps}`;

  /** Append the current step's selection to the prefix; commit when no step remains. */
  function advanceTargeting(
    instanceId: InstanceId,
    previous: readonly TargetStepSelection[],
    stepSelection: readonly TargetCandidate[],
  ): void {
    if (targetContext === undefined) return;
    const definition = definitionForInstance(instanceId);
    if (definition === undefined) return;
    const actions = legalPlanningActions(instanceId);
    const view = computeStepView(definition, targetContext, state, actions, previous, stepSelection);
    const stepId = view?.stepId ?? computeStepView(definition, targetContext, state, actions, previous, [])?.stepId;
    if (stepId === undefined) return;
    const previous2 = [...previous, { stepId, selection: [...stepSelection] }];
    const nextView = computeStepView(definition, targetContext, state, actions, previous2, []);
    if (nextView === undefined) {
      const action = commitCompleteAction(actions, previous2);
      if (action !== undefined && (canApply === undefined || canApply(action))) onAction(action);
      setTargeting(undefined);
      setSelectedHandId(undefined);
      setDraggingHandId(undefined);
    } else {
      setTargeting({ instanceId, previous: previous2, current: [] });
    }
  }

  /** Add one picked candidate to the current step, auto-advancing when it fills the step. */
  function pickTargetingCandidate(
    instanceId: InstanceId,
    previous: readonly TargetStepSelection[],
    current: readonly TargetCandidate[],
    candidate: TargetCandidate,
  ): void {
    if (targetContext === undefined) return;
    const definition = definitionForInstance(instanceId);
    if (definition === undefined) return;
    const view = computeStepView(definition, targetContext, state, legalPlanningActions(instanceId), previous, current);
    if (view === undefined) return;
    const current2 = [...current, candidate];
    if (current2.length >= view.cardinality.max) advanceTargeting(instanceId, previous, current2);
    else setTargeting({ instanceId, previous, current: current2 });
  }

  function startTargeting(instanceId: InstanceId, clickedCellId: CellId): void {
    const view = firstStepView(instanceId);
    const candidate = view?.candidateCells.get(clickedCellId);
    if (candidate === undefined) return;
    pickTargetingCandidate(instanceId, [], [], candidate);
  }

  function handleTargetingCellClick(clickedCellId: CellId): void {
    if (activeTargeting === undefined || targetingView === undefined) return;
    const alreadySelected = targetingView.selectedCells.get(clickedCellId);
    if (alreadySelected !== undefined) {
      const removedKey = targetCandidateKey(alreadySelected);
      setTargeting({
        ...activeTargeting,
        current: activeTargeting.current.filter((entry) => targetCandidateKey(entry) !== removedKey),
      });
      return;
    }
    const candidate = targetingView.candidateCells.get(clickedCellId);
    if (candidate === undefined) return;
    pickTargetingCandidate(activeTargeting.instanceId, activeTargeting.previous, activeTargeting.current, candidate);
  }

  function handleTargetingConfirm(): void {
    if (activeTargeting === undefined || targetingView === undefined || !targetingView.canConfirm) return;
    advanceTargeting(activeTargeting.instanceId, activeTargeting.previous, activeTargeting.current);
  }

  function handleTargetingCancel(): void {
    setTargeting(undefined);
  }

  /** Returns true only when the drop actually staged the card (PlanCard sent). */
  function planAtCell(instanceId: InstanceId, clickedCellId: CellId): boolean {
    if (planningDisabled) return false;
    const directActions = directBoardActions(instanceId);
    if (directActions === undefined) {
      // Targetless card: the board-stage handler owns the single dispatch for
      // the whole "anywhere on the board" surface, so a cell click only bubbles.
      if (directUntargetedAction(instanceId) !== undefined) return false;
      // Multi-step board targeting: drop on the first target, tap the rest.
      if (isBoardTargetable(instanceId)) {
        setSelectedHandId(undefined);
        setDraggingHandId(undefined);
        startTargeting(instanceId, clickedCellId);
        return false;
      }
      // Contracts that cannot be expressed on the board remain unavailable.
      setSelectedHandId(instanceId);
      setDraggingHandId(undefined);
      return false;
    }
    const action = directActions.get(clickedCellId);
    if (action !== undefined && (canApply === undefined || canApply(action))) {
      if (draggingHandId === instanceId || draggingStagedId === instanceId) {
        expectCardFlight(instanceId, { kind: 'staged', cellId: clickedCellId });
      }
      onAction(action);
      setSelectedHandId(undefined);
      setDraggingHandId(undefined);
      setDraggingStagedId(undefined);
      return true;
    }
    return false;
  }

  function handleCellClick(clickedCellId: CellId): void {
    if (planningDisabled) return;
    if (activeTargeting !== undefined) {
      handleTargetingCellClick(clickedCellId);
      return;
    }
    if (selectedHandId !== undefined) planAtCell(selectedHandId, clickedCellId);
  }

  function unplanCard(instanceId: InstanceId): void {
    if (planningDisabled) return;
    const action: Action = { kind: 'UnplanCard', playerId: viewerId, instanceId };
    if (canApply === undefined || canApply(action)) {
      if (draggingStagedId === instanceId) expectCardFlight(instanceId, { kind: 'hand' });
      onAction(action);
    }
  }

  /** Resolves a pointer drop and records any committed destination for the shared card flight. */
  function handlePointerDrop(instanceId: InstanceId, clientX: number, clientY: number): boolean {
    if (planningDisabled) {
      setDraggingHandId(undefined);
      return false;
    }
    // The resolver the hand highlight uses, so the cell that lit up under the
    // finger is the cell this commits to — see ./dropZones.ts.
    const target = resolveDropCandidate(clientX, clientY);
    let played = false;
    if (directUntargetedAction(instanceId) !== undefined) {
      // Targetless card: any point of the board stage activates it.
      if ((target?.closest('[data-testid="board-stage"]') ?? null) !== null) {
        played = playUntargeted(instanceId);
      }
    } else if (directBoardActions(instanceId) !== undefined) {
      const cell = target?.closest<HTMLElement>('[data-cell-id]');
      const cellId = cell?.dataset.cellId as CellId | undefined;
      if (cellId !== undefined && state.board.cells[cellId] !== undefined) {
        played = planAtCell(instanceId, cellId);
      }
    } else if (directBoardTableActions(instanceId) !== undefined) {
      const table = target?.closest<HTMLElement>('[data-table-id]');
      const raw = table?.dataset.tableId;
      const tableId = raw === undefined ? undefined : (Number.parseInt(raw, 10) as TableId);
      if (tableId !== undefined && directBoardTableActions(instanceId)?.has(tableId) === true) {
        played = planAtTable(instanceId, tableId);
      }
    } else if (isBoardTargetable(instanceId)) {
      // Touch drop on the first target starts the on-board targeting session.
      const cell = target?.closest<HTMLElement>('[data-cell-id]');
      const cellId = cell?.dataset.cellId as CellId | undefined;
      if (cellId !== undefined && state.board.cells[cellId] !== undefined) planAtCell(instanceId, cellId);
    }
    setDraggingHandId(undefined);
    return played;
  }

  function handleStagedPointerDrop(instanceId: InstanceId, clientX: number, clientY: number): void {
    if (!planningDisabled) {
      const target = resolveDropCandidate(clientX, clientY);
      const cell = target?.closest<HTMLElement>('[data-cell-id]');
      const cellId = cell?.dataset.cellId as CellId | undefined;
      if (cellId !== undefined && directBoardActions(instanceId)?.has(cellId) === true) {
        planAtCell(instanceId, cellId);
        return;
      }
      const hand = target?.closest('[data-hand-drop-zone="true"]');
      if (hand !== null && hand !== undefined) unplanCard(instanceId);
    }
    setDraggingStagedId(undefined);
  }

  const readyAction: Action = { kind: 'Ready', playerId: viewerId };
  const readyDisabled = planningDisabled || (canApply !== undefined && !canApply(readyAction));
  // «Пора» (02 §5.2): something is planned, or nothing in hand can be.
  const viewerStagedIds = new Set(viewerPlayer?.staged.map((placement) => placement.instanceId) ?? []);
  const readyNudge = !readyDisabled && (viewerStagedIds.size > 0 || handCards.every((card) =>
    viewerStagedIds.has(card.instance.id) || (card.cost !== undefined && card.cost > remainingEnergy)));

  function handleReady(): void {
    if (readyDisabled) return;
    onAction(readyAction);
  }

  function handleChoiceConfirm(tokens: readonly string[]): void {
    if (pendingChoice === undefined) return;
    onAction({
      kind: 'Resolve',
      playerId: viewerId,
      choiceId: pendingChoice.choiceId,
      selection: { tokens },
    });
  }

  const showChoice = pendingChoice !== undefined && pendingChoice.who === viewerId;
  // Targetless card in flight: the whole board stage is the drop/click target.
  const untargetedDraggingId = draggingHandId !== undefined
    && directUntargetedAction(draggingHandId) !== undefined ? draggingHandId : undefined;
  const untargetedSelectedId = selectedHandId !== undefined
    && directUntargetedAction(selectedHandId) !== undefined ? selectedHandId : undefined;
  const untargetedActiveId = untargetedDraggingId ?? untargetedSelectedId;
  const matchResultActions = useContext(MatchResultActionsContext);
  const matchResult = state.status.phase === 'ended'
    ? state.status.winner === 'draw'
      ? {
          outcome: 'draw',
          title: 'НИЧЬЯ',
          detail: 'Оба игрока потеряли всё здоровье',
          color: COLORS.textOnDark,
          step: COLORS.panelEdge,
        }
      : state.status.winner === viewerId
        ? {
            outcome: 'win',
            title: 'ВЫ ПОБЕДИЛИ',
            detail: 'У соперника закончилось здоровье',
            color: COLORS.selection,
            step: COLORS.attack,
          }
        : {
            outcome: 'lose',
            title: 'ВЫ ПРОИГРАЛИ',
            detail: 'У вас закончилось здоровье',
            color: COLORS.textNegativeOnDark,
            step: COLORS.stepShadow,
          }
    : undefined;
  return (
    <div
      className="cards-game-view"
      data-testid="game-view"
      onContextMenu={(event) => {
        const target = event.target as Element;
        if (typeof target.closest !== 'function' || target.closest('.cards-event-log') === null) {
          event.preventDefault();
        }
      }}
    >
      {matchResult !== undefined ? (
        <div
          className="cards-match-result-overlay"
          data-testid="match-result-overlay"
        >
          {/* A win throws 24 pixel confetti out of the banner (03 §4.14); CSS
              owns where each one falls. */}
          {matchResult.outcome === 'win' ? (
            <span className="cards-match-confetti" aria-hidden="true">
              {Array.from({ length: MATCH_CONFETTI_COUNT }, (_, index) => <span key={index} />)}
            </span>
          ) : null}
          <div
            className="cards-match-result-banner"
            data-testid="match-result-banner"
            data-outcome={matchResult.outcome}
            role="alert"
            aria-live="assertive"
            // Result tone depends on the winner; static banner presentation is in KIT_CSS.
            style={{
              '--cards-match-result-color': matchResult.color,
              '--cards-match-result-step': matchResult.step,
            } as CSSProperties}
          >
            <div className="cards-match-result-title">
              {matchResult.title}
            </div>
            <div className="cards-match-result-detail">
              {matchResult.detail}
            </div>
            {matchResultActions}
          </div>
        </div>
      ) : null}
      <div className="cards-game-main">
        <div className="cards-player-scoreboard cards-scoreboard-opponent" data-testid="player-scoreboard">
          {opponentPlayer !== undefined ? (
            <PlayerPanel
              player={opponentPlayer}
              isViewer={false}
              testId="player-panel-opponent"
              displayName={playerNames?.[opponentPlayer.id]}
              deckPassive={opponentDeckPassive}
              showTokenDeaths={opponentMechanics?.tracksTokenDeaths === true}
              ready={state.phase === 'planning' && opponentPlayer.ready}
            />
          ) : null}
        </div>
        <div
          className="cards-board-stage"
          data-testid="board-stage"
          data-onboarding="board"
          data-untargeted-active={untargetedActiveId !== undefined ? 'true' : undefined}
          data-untargeted-drop-enabled={untargetedDraggingId !== undefined ? 'true' : undefined}
          onClick={untargetedSelectedId === undefined ? undefined : () => playUntargeted(untargetedSelectedId)}
        >
          <PlaybackHud round={state.round} planning={state.phase === 'planning'} />
          <Board
            state={state}
            viewerId={viewerId}
            viewerSide={viewerSide}
            opponentSide={opponentSide}
            selectedHandId={selectedHandId}
            draggingHandId={draggingHandId}
            draggingStagedId={draggingStagedId}
            playerNames={playerNames}
            stagedByCell={stagedByCell}
            planningDisabled={planningDisabled}
            canApply={canApply}
            onCellClick={handleCellClick}
            onStagedPointerDragStart={(instanceId) => {
              if (planningDisabled) return;
              setSelectedHandId(undefined);
              setDraggingHandId(undefined);
              setDraggingStagedId(instanceId);
            }}
            onStagedPointerDrop={handleStagedPointerDrop}
            onStagedPointerDragEnd={() => setDraggingStagedId(undefined)}
            onStagedKeyboardUnplan={(instanceId) => {
              if (!planningDisabled) unplanCard(instanceId);
            }}
            intentForCell={intentForCell}
            intentForTable={intentForTable}
            onTableClick={handleTableClick}
            targeting={activeTargeting === undefined || targetingView === undefined ? undefined : {
              candidateCells: new Set(targetingView.candidateCells.keys()),
              selectedCells: new Set(targetingView.selectedCells.keys()),
            }}
            onTargetingCellClick={handleTargetingCellClick}
            flights={flightApi}
            opponentSpellQueue={(
              <SpellPlan
                plans={[]}
                state={state}
                disabled
                side="opponent"
                playbackCards={opponentLineup}
                onCancel={() => undefined}
              />
            )}
            viewerSpellQueue={(
              <SpellPlan
                plans={viewerPlayer.staged}
                state={state}
                disabled={planningDisabled}
                visibleKinds={viewerMechanics.plannedCardKinds}
                playbackCards={viewerLineup}
                onCancel={(instanceId) => {
                  if (planningDisabled) return;
                  const action: Action = { kind: 'UnplanCard', playerId: viewerId, instanceId };
                  if (canApply === undefined || canApply(action)) onAction(action);
                }}
              />
            )}
          />
          {activeTargeting !== undefined && targetingView !== undefined ? (
            <div className="cards-targeting-bar" data-testid="targeting-bar" role="status" aria-live="polite">
              <span className="cards-targeting-bar-label" title={targetingStepLabel}>{targetingStepLabel}</span>
              {targetingView.candidateModes.map((candidate) => (
                <Button
                  key={candidate.id}
                  variant="secondary"
                  size="sm"
                  className="cards-targeting-mode"
                  onClick={() => pickTargetingCandidate(
                    activeTargeting.instanceId,
                    activeTargeting.previous,
                    activeTargeting.current,
                    candidate,
                  )}
                >
                  {getCardModeLabel(
                    state.instances[activeTargeting.instanceId]?.defId ?? '',
                    candidate.id,
                  )?.label ?? candidate.id}
                </Button>
              ))}
              {targetingView.cardinality.max > 1 ? (
                <span className="cards-targeting-bar-count" data-testid="targeting-count">
                  Выбрано {activeTargeting.current.length} из {targetingView.cardinality.max}
                </span>
              ) : null}
              <Button
                variant={targetingView.canConfirm ? 'primary' : 'secondary'}
                size="sm"
                className="cards-targeting-confirm"
                data-testid="targeting-confirm"
                disabled={!targetingView.canConfirm}
                onClick={handleTargetingConfirm}
              >
                Подтвердить
              </Button>
              <Button
                variant="secondary"
                size="sm"
                className="cards-targeting-cancel"
                data-testid="targeting-cancel"
                onClick={handleTargetingCancel}
              >
                Отменить
              </Button>
            </div>
          ) : null}
        </div>
        <div className="cards-player-scoreboard cards-scoreboard-mine" data-testid="player-scoreboard-mine">
          <PlayerPanel
            player={viewerPlayer}
            isViewer={true}
            testId="player-panel-you"
            displayName={playerNames?.[viewerId]}
            deckPassive={viewerDeckPassive}
            spendableEnergy={remainingEnergy}
            showTokenDeaths={viewerMechanics.tracksTokenDeaths}
            onDeckOpen={openDeckViewer}
            ready={state.phase === 'planning' && viewerPlayer.ready}
          />
        </div>
        <div className="cards-hand-area">
          <Hand
            cards={handCards}
            stagedIds={stagedHandIds}
            remainingEnergy={remainingEnergy}
            selectedId={selectedHandId}
            draggingId={draggingHandId}
            planningDisabled={planningDisabled}
            onSelect={selectHandCard}
            onPointerDragStart={(instanceId) => {
              if (planningDisabled) return;
              setTargeting(undefined);
              setSelectedHandId(undefined);
              setDraggingStagedId(undefined);
              setDraggingHandId(instanceId);
            }}
            onPointerDrop={handlePointerDrop}
            onPointerDragEnd={() => setDraggingHandId(undefined)}
            returnDropId={draggingStagedId}
            flights={flightApi}
            drawCue={viewerDrawCue}
          />
        </div>
        <ReadyButton
          disabled={readyDisabled}
          waiting={viewerPlayer?.ready === true && state.phase === 'planning' && !playbackIsPlaying}
          nudge={readyNudge}
          onClick={handleReady}
        />
      </div>
      <EventLog
        log={log}
        matchId={state.matchId}
        viewerId={viewerId}
        viewerSide={viewerSide}
        {...(playerNames !== undefined ? { playerNames } : {})}
      />
      {cardFlights.map((flight) => (
        <CardFlightGhost
          key={flight.id}
          flight={flight}
          onLanding={landCardFlight}
          onDone={finishCardFlight}
        />
      ))}
      {deckOpen ? (
        <DeckViewer state={state} instanceIds={viewerPlayer.deck} onClose={closeDeckViewer} />
      ) : null}
      {showChoice && pendingChoice !== undefined ? (
        <ChoicePrompt key={pendingChoice.choiceId} choice={pendingChoice} onConfirm={handleChoiceConfirm} />
      ) : null}
    </div>
  );
}
